// Per-user UI appearance (2026-09-01). The preference lives on the account
// (users.theme); localStorage mirrors it only so the pre-auth paint and page
// reloads don't flash the wrong theme. "system" follows the device via
// matchMedia and tracks live changes.

export type ThemePref = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "shobdo_theme";

const isPref = (v: unknown): v is ThemePref =>
  v === "light" || v === "dark" || v === "system";

export const storedThemePref = (): ThemePref => {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return isPref(v) ? v : "system";
  } catch {
    return "system";
  }
};

const systemQuery = (): MediaQueryList | null =>
  typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

const resolve = (pref: ThemePref): "light" | "dark" => {
  if (pref !== "system") return pref;
  return systemQuery()?.matches ? "dark" : "light";
};

let systemListener: ((e: MediaQueryListEvent) => void) | null = null;

/** Paint the document with the preference and keep following the device while
 *  the preference is "system". Also mirrors the pref to localStorage. */
export const applyThemePref = (pref: ThemePref): void => {
  const safe = isPref(pref) ? pref : "system";
  const resolved = resolve(safe);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.bsTheme = resolved; // Bootstrap 5.3 components
  try {
    localStorage.setItem(THEME_STORAGE_KEY, safe);
  } catch {
    // storage unavailable — the paint still happened
  }
  const q = systemQuery();
  if (q && systemListener) {
    q.removeEventListener("change", systemListener);
    systemListener = null;
  }
  if (q && safe === "system") {
    systemListener = (e) => {
      const mode = e.matches ? "dark" : "light";
      document.documentElement.dataset.theme = mode;
      document.documentElement.dataset.bsTheme = mode;
    };
    q.addEventListener("change", systemListener);
  }
};
